import {
  collection,
  doc,
  addDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { assertNotImpersonating } from '../utils/impersonationGuard';
import { dnsLookupService, DNSVerificationResult } from './dnsLookupService';
import { sesIdentityService, SESDNSRecord } from './sesIdentityService';

export interface DNSRecord {
  type: 'TXT' | 'CNAME' | 'MX' | 'A';
  name: string;
  value: string;
  status: 'verified' | 'pending' | 'failed';
  verifiedAt?: Timestamp;
}

export interface DomainVerification {
  id: string;
  userId: string;
  workspaceId?: string;
  domain: string;
  status: 'pending' | 'verified' | 'failed' | 'warming';
  dnsRecords: DNSRecord[];
  verificationToken: string;
  createdAt: Timestamp;
  verifiedAt?: Timestamp;
  lastCheckedAt?: Timestamp;
  warmingInfo?: {
    day: number;
    dailyCount: number;
    startedAt: Timestamp;
    lastActiveAt: Timestamp | null;
  };
}

class DomainVerificationService {
  private collectionName = 'domainVerifications';

  // Generate unique verification token for domain
  private generateVerificationToken(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  // Generate DNS records for domain verification
  private generateDNSRecords(domain: string, verificationToken: string): DNSRecord[] {
    // Generate a consistent domain ID based on the domain name
    const domainId = this.generateDomainId(domain);

    // Simplified DNS records - just the 3 essential records for email deliverability
    const records: DNSRecord[] = [
      // 1. SPF record - authorizes Scubamail to send emails for this domain
      {
        type: 'TXT' as const,
        name: domain,
        value: 'v=spf1 include:spf.scubamail.io ~all',
        status: 'pending' as const
      },
      // 2. DKIM record - email signature authentication
      {
        type: 'CNAME' as const,
        name: `sm._domainkey.${domain}`,
        value: `sm._domainkey.${domainId}.scubamail.io`,
        status: 'pending' as const
      },
      // 3. DMARC record - policy for handling authentication failures
      {
        type: 'TXT' as const,
        name: `_dmarc.${domain}`,
        value: 'v=DMARC1; p=none;',
        status: 'pending' as const
      }
    ];

    console.log('Generated DNS records for domain:', domain, records);
    return records;
  }

  // Generate a consistent domain ID based on the domain name
  private generateDomainId(domain: string): string {
    // Create a consistent hash from the domain name
    let hash = 0;
    for (let i = 0; i < domain.length; i++) {
      const char = domain.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    // Convert to positive and create a 10-character alphanumeric string
    const positiveHash = Math.abs(hash);
    return positiveHash.toString(36).substring(0, 10);
  }

  // Check for existing DNS records on the domain
  private async checkExistingDNSRecords(domain: string): Promise<DNSRecord[]> {
    const existingRecords: DNSRecord[] = [];

    try {
      // Check for DMARC record
      const dmarcResult = await dnsLookupService.lookupTXT(`_dmarc.${domain}`);
      if (dmarcResult.success && dmarcResult.records.length > 0) {
        // Find the DMARC record
        const dmarcRecord = dmarcResult.records.find(record =>
          record.includes('v=DMARC1')
        );
        if (dmarcRecord) {
          existingRecords.push({
            type: 'TXT',
            name: `_dmarc.${domain}`,
            value: dmarcRecord.replace(/^"|"$/g, ''), // Remove quotes
            status: 'verified'
          });
        }
      }

      // Check for SPF record
      const spfResult = await dnsLookupService.lookupTXT(domain);
      if (spfResult.success && spfResult.records.length > 0) {
        const spfRecord = spfResult.records.find(record =>
          record.includes('v=spf1')
        );
        if (spfRecord) {
          existingRecords.push({
            type: 'TXT',
            name: domain,
            value: spfRecord.replace(/^"|"$/g, ''),
            status: 'verified'
          });
        }
      }

      // Check for DKIM records (common patterns)
      const dkimPatterns = [
        `default._domainkey.${domain}`,
        `mail._domainkey.${domain}`,
        `google._domainkey.${domain}`,
        `k1._domainkey.${domain}`
      ];

      for (const dkimName of dkimPatterns) {
        const dkimResult = await dnsLookupService.lookupTXT(dkimName);
        if (dkimResult.success && dkimResult.records.length > 0) {
          const dkimRecord = dkimResult.records.find(record =>
            record.includes('v=DKIM1')
          );
          if (dkimRecord) {
            existingRecords.push({
              type: 'TXT',
              name: dkimName,
              value: dkimRecord.replace(/^"|"$/g, ''),
              status: 'verified'
            });
          }
        }
      }
    } catch (error) {
      console.warn('Error checking existing DNS records:', error);
      // Continue without existing records
    }

    return existingRecords;
  }

  // Create domain verification request
  // This now also registers the domain in AWS SES to get real DKIM tokens
  async createDomainVerification(userId: string, domain: string, workspaceId?: string): Promise<string> {
    assertNotImpersonating('verify domain');
    try {
      // Global availability check - prevent claiming a domain owned by someone else
      const availability = await this.checkDomainAvailability(domain, { userId, workspaceId });
      if (!availability.isAvailable) {
        throw new Error(`Domain ${domain} is already registered by another account. Please use a different domain or contact support if you believe this is an error.`);
      }

      // Check if domain already exists for this user in this workspace (idempotency)
      const existingVerification = await this.getDomainVerificationByDomain(userId, domain, workspaceId);
      if (existingVerification) {
        // For existing domains, try to refresh SES registration
        try {
          const sesResult = await sesIdentityService.registerDomain(domain);
          const sesRecords = this.convertSESDNSRecords(sesResult.dnsRecords);

          await updateDoc(doc(db, this.collectionName, existingVerification.id), {
            dnsRecords: sesRecords,
            verificationToken: sesResult.verificationToken,
            dkimTokens: sesResult.dkimTokens,
            sesRegistered: true,
            lastCheckedAt: Timestamp.now()
          });
        } catch (sesError) {
          console.warn('Failed to refresh SES registration, using local records:', sesError);
          // Fallback to local record generation
          const verificationToken = this.generateVerificationToken();
          const newRecords = this.generateDNSRecords(domain, verificationToken);

          await updateDoc(doc(db, this.collectionName, existingVerification.id), {
            dnsRecords: newRecords,
            verificationToken,
            lastCheckedAt: Timestamp.now()
          });
        }

        return existingVerification.id;
      }

      // Register domain in AWS SES first to get real DKIM tokens
      let dnsRecords: DNSRecord[];
      let verificationToken: string;
      let dkimTokens: string[] = [];
      let sesRegistered = false;

      try {
        console.log('📧 Registering domain in AWS SES:', domain);
        const sesResult = await sesIdentityService.registerDomain(domain);
        console.log('✅ SES registration successful:', sesResult);

        verificationToken = sesResult.verificationToken;
        dkimTokens = sesResult.dkimTokens;
        dnsRecords = this.convertSESDNSRecords(sesResult.dnsRecords);
        sesRegistered = true;
      } catch (sesError) {
        console.warn('⚠️ SES registration failed, falling back to local records:', sesError);
        // Fallback to local record generation if SES fails
        verificationToken = this.generateVerificationToken();
        dnsRecords = this.generateDNSRecords(domain, verificationToken);
      }

      console.log('Creating domain verification with records:', {
        domain,
        sesRegistered,
        recordCount: dnsRecords.length
      });

      const verificationData: Omit<DomainVerification, 'id'> & { dkimTokens?: string[]; sesRegistered?: boolean } = {
        userId,
        workspaceId,
        domain: domain.toLowerCase(),
        status: 'pending',
        dnsRecords,
        verificationToken,
        dkimTokens,
        sesRegistered,
        createdAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, this.collectionName), verificationData);
      console.log('Created domain verification with ID:', docRef.id);
      return docRef.id;
    } catch (error: any) {
      console.error('Error creating domain verification:', error);
      throw new Error(error.message || 'Failed to create domain verification');
    }
  }

  // Convert SES DNS records to our DNSRecord format
  private convertSESDNSRecords(sesRecords: SESDNSRecord[]): DNSRecord[] {
    return sesRecords.map(record => ({
      type: record.type as 'TXT' | 'CNAME' | 'MX' | 'A',
      name: record.name,
      value: record.value,
      status: 'pending' as const
    }));
  }

  // Get domain verification by ID
  async getDomainVerification(verificationId: string): Promise<DomainVerification | null> {
    try {
      const docRef = doc(db, this.collectionName, verificationId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as DomainVerification;
      }
      return null;
    } catch (error) {
      console.error('Error getting domain verification:', error);
      throw new Error('Failed to get domain verification');
    }
  }

  // Get domain verification by domain name
  async getDomainVerificationByDomain(userId: string, domain: string, workspaceId?: string): Promise<DomainVerification | null> {
    try {
      const constraints = [
        where('userId', '==', userId),
        where('domain', '==', domain.toLowerCase())
      ];

      if (workspaceId) {
        constraints.push(where('workspaceId', '==', workspaceId));
      }

      const q = query(collection(db, this.collectionName), ...constraints);

      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() } as DomainVerification;
      }
      return null;
    } catch (error) {
      console.error('Error getting domain verification by domain:', error);
      throw new Error('Failed to get domain verification');
    }
  }

  // Check if domain is already taken by any user
  async checkDomainAvailability(domain: string, options?: { userId?: string; workspaceId?: string }): Promise<{
    isAvailable: boolean;
    takenByUserId?: string;
    takenByUserEmail?: string;
  }> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('domain', '==', domain.toLowerCase())
      );

      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const matchingDocs = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as DomainVerification));

        // A domain is available ONLY if:
        // 1. It doesn't exist at all (matchingDocs is empty)
        // 2. OR it belongs to the SAME workspace (if workspaceId is provided)
        // 3. OR it belongs to the SAME user (if they are re-adding it to the same workspace)
        
        const ownedByCurrentUserInThisWorkspace = matchingDocs.some((verification) => {
          const sameUser = options?.userId && verification.userId === options.userId;
          const sameWorkspace = options?.workspaceId && verification.workspaceId === options.workspaceId;
          return sameUser && sameWorkspace;
        });

        if (ownedByCurrentUserInThisWorkspace) {
          return { isAvailable: true };
        }

        // If it exists but is not owned by the current user in this specific workspace, it's NOT available.
        const firstMatch = matchingDocs[0];
        return {
          isAvailable: false,
          takenByUserId: firstMatch.userId,
          takenByUserEmail: undefined
        };
      }
      return { isAvailable: true };
    } catch (error) {
      console.error('Error checking domain availability:', error);
      // If we can't check, assume it's available to not block users
      return { isAvailable: true };
    }
  }

  // Get all domain verifications for a user
  async getUserDomainVerifications(userId: string, workspaceId?: string): Promise<DomainVerification[]> {
    try {
      console.log('Getting domain verifications for user:', userId);
      const constraints = [where('userId', '==', userId)];
      if (workspaceId) {
        constraints.push(where('workspaceId', '==', workspaceId));
      }

      const q = query(collection(db, this.collectionName), ...constraints);

      const querySnapshot = await getDocs(q);
      console.log('Query snapshot size:', querySnapshot.docs.length);
      const results = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as DomainVerification[];

      // Sort by createdAt on the client side to avoid needing a composite index
      results.sort((a, b) => {
        const aTime = a.createdAt?.toMillis() || 0;
        const bTime = b.createdAt?.toMillis() || 0;
        return bTime - aTime; // Descending order (newest first)
      });

      console.log('Domain verifications found:', results);
      return results;
    } catch (error: any) {
      console.error('Error getting user domain verifications:', error);
      console.error('Error details:', {
        code: error?.code,
        message: error?.message,
        stack: error?.stack
      });

      // If it's a permission error or collection doesn't exist, return empty array
      if (error?.code === 'permission-denied' ||
        error?.code === 'not-found' ||
        error?.message?.includes('Missing or insufficient permissions') ||
        error?.message?.includes('collection')) {
        console.log('No permission or collection not found, returning empty array');
        return [];
      }

      throw new Error(`Failed to get domain verifications: ${error?.message || 'Unknown error'}`);
    }
  }

  // Verify DNS records using real DNS lookup
  async verifyDNSRecords(verificationId: string): Promise<{ success: boolean; message: string; verifiedRecords: string[]; verificationResults: DNSVerificationResult[] }> {
    // Persists the verification outcome, so it counts as a write.
    assertNotImpersonating('verify DNS records');
    try {
      const verification = await this.getDomainVerification(verificationId);
      if (!verification) {
        return {
          success: false,
          message: 'Verification not found',
          verifiedRecords: [],
          verificationResults: []
        };
      }

      // Check if domain exists first
      const domainExists = await dnsLookupService.checkDomainExists(verification.domain);
      if (!domainExists) {
        return {
          success: false,
          message: `Domain ${verification.domain} does not exist or is not reachable`,
          verifiedRecords: [],
          verificationResults: []
        };
      }

      // Verify all DNS records using real DNS lookup
      const verificationResults = await dnsLookupService.verifyRecords(verification.dnsRecords);

      // Update records based on verification results
      const updatedRecords = verification.dnsRecords.map(record => {
        const result = verificationResults.find(r => r.recordName === record.name);
        if (result && result.verified) {
          return {
            ...record,
            status: 'verified' as const,
            verifiedAt: Timestamp.now()
          };
        } else if (result && !result.verified) {
          return {
            ...record,
            status: 'failed' as const
          };
        }
        return record;
      });

      // Count verified records
      const verifiedRecords = updatedRecords
        .filter(record => record.status === 'verified')
        .map(record => record.name);

      // Check if all required records are verified
      const allVerified = updatedRecords.every(record => record.status === 'verified');
      const hasFailures = updatedRecords.some(record => record.status === 'failed');

      let newStatus: 'pending' | 'verified' | 'failed';
      if (allVerified) {
        newStatus = 'verified';
      } else if (hasFailures) {
        newStatus = 'failed';
      } else {
        newStatus = 'pending';
      }

      const verifiedAt = allVerified ? Timestamp.now() : undefined;

      // Update the verification record
      await updateDoc(doc(db, this.collectionName, verificationId), {
        dnsRecords: updatedRecords,
        status: newStatus,
        verifiedAt,
        lastCheckedAt: Timestamp.now()
      });

      // Generate appropriate message
      let message: string;
      if (allVerified) {
        message = 'Domain verification completed successfully! All DNS records are properly configured.';
      } else if (hasFailures) {
        const failedRecords = verificationResults.filter(r => !r.verified);
        message = `Verification failed. ${failedRecords.length} DNS record(s) are missing or incorrect.`;
      } else {
        message = `Some DNS records are still pending. Verified: ${verifiedRecords.length}/${updatedRecords.length}. Please wait for DNS propagation and try again.`;
      }

      return {
        success: allVerified,
        message,
        verifiedRecords,
        verificationResults
      };
    } catch (error: any) {
      console.error('Error verifying DNS records:', error);
      return {
        success: false,
        message: `Failed to verify DNS records: ${error.message}`,
        verifiedRecords: [],
        verificationResults: []
      };
    }
  }

  // Update domain verification status
  async updateDomainStatus(verificationId: string, status: 'pending' | 'verified' | 'failed'): Promise<void> {
    assertNotImpersonating('update domain status');
    try {
      const updateData: any = { status };

      if (status === 'verified') {
        updateData.verifiedAt = Timestamp.now();
      }

      updateData.lastCheckedAt = Timestamp.now();

      await updateDoc(doc(db, this.collectionName, verificationId), updateData);
    } catch (error) {
      console.error('Error updating domain status:', error);
      throw new Error('Failed to update domain status');
    }
  }

  // Delete domain verification
  async deleteDomainVerification(verificationId: string): Promise<void> {
    assertNotImpersonating('delete domain verification');
    try {
      const verification = await this.getDomainVerification(verificationId);
      if (verification) {
        console.log(`🗑️ Deleting domain ${verification.domain} from SES and Firestore...`);
        // Attempt to delete from SES first
        try {
          await sesIdentityService.deleteDomain(verification.domain);
          console.log(`✅ Successfully removed ${verification.domain} from SES`);
        } catch (sesError) {
          console.warn(`⚠️ Failed to remove ${verification.domain} from SES, proceeding with Firestore deletion:`, sesError);
        }
      }

      await deleteDoc(doc(db, this.collectionName, verificationId));
      console.log(`✅ Successfully removed domain verification record ${verificationId}`);
    } catch (error: any) {
      console.error('Error deleting domain verification:', error);
      throw new Error(error.message || 'Failed to delete domain verification');
    }
  }

  // Refresh DNS records for an existing verification - now uses SES
  async refreshDomainVerification(verificationId: string): Promise<void> {
    // Rewrites the stored DNS records, so it counts as a write. Callers wrap this
    // in try/catch, so the DNS config page still renders the existing records.
    assertNotImpersonating('refresh domain verification');
    try {
      const verification = await this.getDomainVerification(verificationId);
      if (!verification) {
        throw new Error('Verification not found');
      }

      // Try to register/refresh in SES first
      let dnsRecords: DNSRecord[];
      let verificationToken = verification.verificationToken;
      let dkimTokens: string[] = [];
      let sesRegistered = false;

      try {
        console.log('📧 Registering domain in AWS SES:', verification.domain);
        const sesResult = await sesIdentityService.registerDomain(verification.domain);
        console.log('✅ SES registration successful:', sesResult);

        verificationToken = sesResult.verificationToken;
        dkimTokens = sesResult.dkimTokens;
        dnsRecords = this.convertSESDNSRecords(sesResult.dnsRecords);
        sesRegistered = true;
      } catch (sesError) {
        console.warn('⚠️ SES registration failed, using local records:', sesError);
        // Fallback to local record generation
        dnsRecords = this.generateDNSRecords(verification.domain, verificationToken);
      }

      console.log('Refreshing domain verification with records:', {
        verificationId,
        domain: verification.domain,
        sesRegistered,
        recordCount: dnsRecords.length
      });

      // Update the verification with fresh records
      await updateDoc(doc(db, this.collectionName, verificationId), {
        dnsRecords,
        verificationToken,
        dkimTokens,
        sesRegistered,
        lastCheckedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error refreshing domain verification:', error);
      throw new Error('Failed to refresh domain verification');
    }
  }

  // Get detailed verification results for a domain
  async getDetailedVerificationResults(verificationId: string): Promise<{
    verification: DomainVerification | null;
    verificationResults: DNSVerificationResult[];
    domainExists: boolean;
  }> {
    try {
      const verification = await this.getDomainVerification(verificationId);
      if (!verification) {
        return {
          verification: null,
          verificationResults: [],
          domainExists: false
        };
      }

      const domainExists = await dnsLookupService.checkDomainExists(verification.domain);
      const verificationResults = await dnsLookupService.verifyRecords(verification.dnsRecords);

      return {
        verification,
        verificationResults,
        domainExists
      };
    } catch (error) {
      console.error('Error getting detailed verification results:', error);
      throw new Error('Failed to get detailed verification results');
    }
  }

  // Check if an email's domain is verified for a given user
  async isEmailDomainVerified(userId: string, email: string, workspaceId?: string): Promise<{
    isVerified: boolean;
    domain?: string;
    verification?: DomainVerification;
  }> {
    try {
      // Extract domain from email
      const emailParts = email.split('@');
      if (emailParts.length !== 2) {
        return { isVerified: false };
      }

      const domain = emailParts[1].toLowerCase();

      // Check for common free email providers that can't be verified as custom domains
      const freeEmailDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 'live.com', 'msn.com', 'icloud.com', 'me.com', 'mac.com'];
      if (freeEmailDomains.includes(domain)) {
        return { isVerified: false, domain };
      }

      // Look up domain verification for this user
      const verification = await this.getDomainVerificationByDomain(userId, domain, workspaceId);

      if (verification && verification.status === 'verified') {
        console.log(`📧 Domain ${domain} is verified for user ${userId}`);
        return {
          isVerified: true,
          domain,
          verification
        };
      }

      return { isVerified: false, domain };
    } catch (error) {
      console.error('Error checking if email domain is verified:', error);
      return { isVerified: false };
    }
  }

  // Get DNS instructions for a domain
  getDNSInstructions(domain: string, verificationToken: string): {
    verifiedRecords: DNSRecord[];
    pendingRecords: DNSRecord[];
    instructions: string[];
  } {
    const dnsRecords = this.generateDNSRecords(domain, verificationToken);

    const verifiedRecords = dnsRecords.filter(record => record.status === 'verified');
    const pendingRecords = dnsRecords.filter(record => record.status === 'pending');

    const instructions = [
      'Log in to your domain registrar or DNS provider',
      'Navigate to DNS management or DNS settings',
      'Add the following DNS records exactly as shown',
      'Wait 5-10 minutes for DNS propagation',
      'Click "Complete verification" to check your records'
    ];

    return { verifiedRecords, pendingRecords, instructions };
  }
}

export const domainVerificationService = new DomainVerificationService();
